import { AlertProps } from '@platform-ui/design-system';
import React, {
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import {
  NavigationVariant,
  useNavigationVariant,
} from './hooks/useNavigationVariant';

type NavigationContextProps = {
  setPageAlert: (props: AlertProps) => void;
  pageAlert?: AlertProps;
  variant: NavigationVariant;
  suppressBillingEnvironmentBanner: boolean;
};

export type NavigationOverrides =
  | Partial<{ variant: NavigationVariant }>
  | undefined;

export const NavigationContext = createContext<NavigationContextProps>({
  setPageAlert: () => {
    throw new Error('NavigationContext not initiated');
  },
  variant: 'billing',
  suppressBillingEnvironmentBanner: false,
});

export const NavigationContextProvider = ({
  children,
  overrides,
  suppressBillingEnvironmentBanner,
}: {
  children: ReactNode;
  overrides: NavigationOverrides;
  suppressBillingEnvironmentBanner?: boolean;
}) => {
  const parentContext = useContext(NavigationContext);
  const [pageAlert, setPageAlert] = useState<AlertProps | undefined>();
  const variant = useNavigationVariant(overrides);
  const shouldSuppressBillingEnvironmentBanner =
    suppressBillingEnvironmentBanner ??
    parentContext.suppressBillingEnvironmentBanner;

  const value = useMemo(
    () => ({
      pageAlert,
      setPageAlert,
      variant,
      suppressBillingEnvironmentBanner: shouldSuppressBillingEnvironmentBanner,
    }),
    [pageAlert, setPageAlert, variant, shouldSuppressBillingEnvironmentBanner],
  );

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};
